import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useCart } from './contexts/CartContext.tsx';
import { OrderService, OrderDetails } from './services/OrderService.ts';
import { analyticsService } from './services/AnalyticsService.ts';
import AttributionService from './services/AttributionService.ts';
import BusinessIntelligenceService from './services/BusinessIntelligenceService.ts';
import { trackStackAdaptConversion } from './StackAdapt.tsx';
import OrderDisplay from './components/OrderDisplay.tsx';
import OrderSkeleton from './components/OrderSkeleton.tsx';
import { MENU_ID_TO_STORE } from './config/storeMenuConfig.ts';
import './OrderConfirmation.css';

const OrderConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [orderStatus, setOrderStatus] = useState<'success' | 'failed' | 'pending' | 'loading'>('loading');
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [, setRetryAttempts] = useState(0);
  const [maxRetries] = useState(3);
  const [purchaseTracked, setPurchaseTracked] = useState(false);
  const [successfulRetailerId, setSuccessfulRetailerId] = useState<string | null>(null);

  // Function to track purchase event for Firebase Analytics monetization
  const trackPurchaseEvent = (orderData: OrderDetails) => {
    try {
      // Get attribution data for purchase
      const attributionService = AttributionService.getInstance();
      const attributionData = attributionService.getAttributionData();

      // Get store and pricing type information
      const storedPricingType = localStorage.getItem('cartPricingType') || 'RECREATIONAL';
      const lastMenuAccessed = localStorage.getItem('last_menu_accessed');

      // Determine store information based on menu ID
      let storeName = 'Unknown';
      let storeLocation = 'Unknown';
      if (lastMenuAccessed && MENU_ID_TO_STORE[lastMenuAccessed]) {
        const menuInfo = MENU_ID_TO_STORE[lastMenuAccessed];
        storeName = `${menuInfo.store.charAt(0).toUpperCase() + menuInfo.store.slice(1)} ${menuInfo.type.charAt(0).toUpperCase() + menuInfo.type.slice(1)}`;
        storeLocation = menuInfo.store.charAt(0).toUpperCase() + menuInfo.store.slice(1);
      }

      // Calculate tax amount (cannabis + sales tax)
      const taxAmount = (orderData.taxes?.cannabis || 0) + (orderData.taxes?.sales || 0);

      // Get Dutchie order parameters for tracking
      const dutchieOrderId = searchParams.get('orderId') || searchParams.get('order_id');
      const dutchieOrderNumber = searchParams.get('orderNumber') || searchParams.get('order_number');

      // Map order items to analytics format
      const items = orderData.items?.map(item => ({
        item_id: item.productId || 'unknown',
        item_name: item.product?.name || item.name || 'Unknown Product',
        item_category: item.product?.category || 'Cannabis',
        item_brand: item.product?.brand?.name || 'Unknown Brand',
        quantity: item.quantity || 1,
        price: (item.price || 0) / 100, // Convert cents to dollars
        item_variant: item.product?.subcategory || undefined,
        // Surfside-specific fields
        item_price: (item.price || 0) / 100, // Convert cents to dollars
        item_quantity: item.quantity || 1,
        location_id: lastMenuAccessed || 'unknown'
      })) || [];

      const purchaseEventData = {
        transaction_id: orderData.orderNumber,
        value: orderData.total || 0,
        currency: 'USD',
        tax: taxAmount,
        items: items,
        affiliation: 'Sweetspot Farms',
        // Store and pricing type information
        store_id: lastMenuAccessed || 'unknown',
        store_name: storeName,
        store_location: storeLocation,
        pricing_type: storedPricingType,
        menu_type: storedPricingType.toLowerCase(), // medical or recreational
        // Add Dutchie order tracking as custom properties
        dutchie_order_id: dutchieOrderId || undefined,
        dutchie_order_number: dutchieOrderNumber || undefined,
        order_source: 'dutchie',
        payment_method: orderData.paymentMethod || 'unknown',
        // Revenue Attribution Data
        ...attributionData.utm_attribution,
        session_id: attributionData.sessionId,
        search_attribution: attributionData.search_attribution,
        filter_attribution: attributionData.filter_attribution,
        landing_page: attributionData.landingPage,
        referrer: attributionData.referrer,
        is_first_visit: attributionData.firstVisit,
        session_duration: attributionData.session_insights.session_duration,
        search_count: attributionData.session_insights.search_count,
        filter_count: attributionData.session_insights.filter_count
      };

      // Track the purchase with proper revenue data for monetization
      analyticsService.trackPurchase(purchaseEventData);

      // Track business intelligence purchase event
      const businessIntelligenceService = BusinessIntelligenceService.getInstance();
      businessIntelligenceService.updateShoppingPattern('purchase', orderData.total || 0);

      // Track StackAdapt conversion pixel
      trackStackAdaptConversion(orderData.total || 0, orderData.orderNumber);
    } catch (error) {
      // Analytics failure shouldn't break the confirmation page
    }
  };

  // Function to fetch order details using smart lookup with retry logic
  const fetchOrderDetails = async (orderNumber: string, attempt: number = 1) => {
    setIsLoadingOrder(true);
    setOrderError(null);
    setRetryAttempts(attempt);

    try {
      // Use smart lookup with fallback across retailers
      const { order: orderData, retailerId } = await OrderService.findOrderWithFallback(orderNumber);

      setOrderDetails(orderData);
      setSuccessfulRetailerId(retailerId);
      setOrderError(null);
      setIsLoadingOrder(false);

      // Track purchase for Firebase Analytics monetization (only once)
      if (!purchaseTracked) {
        trackPurchaseEvent(orderData);
        setPurchaseTracked(true);
      }
    } catch (error) {
      if (attempt < maxRetries) {
        // Retry after a delay
        const delay = attempt * 1000; // 1s, 2s, 3s delays
        setTimeout(() => {
          fetchOrderDetails(orderNumber, attempt + 1);
        }, delay);
      } else {
        // All retries exhausted
        setOrderError(error instanceof Error ? error.message : 'Failed to load order details');
        setIsLoadingOrder(false);
      }
    }
  };

  useEffect(() => {
    const processOrderConfirmation = async () => {
      // Check for Dutchie redirect parameters
      const status = searchParams.get('status');
      const orderId = searchParams.get('orderId') || searchParams.get('order_id');
      const orderNumber = searchParams.get('orderNumber') || searchParams.get('order_number');
      const checkoutId = searchParams.get('checkoutId') || searchParams.get('checkout_id');
      const error = searchParams.get('error');

      // Capture ALL parameters from Dutchie redirect
      const allParams = Object.fromEntries(searchParams.entries());

      // Store all parameters in case we need them for display
      if (typeof window !== 'undefined') {
        (window as any).__DUTCHIE_REDIRECT_PARAMS = allParams;
      }

      if (error || status === 'failed') {
        setOrderStatus('failed');
        return;
      } 

      if (status === 'pending') {
        setOrderStatus('pending');
        return;
      }

      // Order was successful
      setOrderStatus('success');

      // Clear the cart since the order was successful
      clearCart();

      // If we have an order number, fetch the full order details
      if (orderNumber) {
        await fetchOrderDetails(orderNumber);
      } else if (orderId) {
        // Fallback to orderId if orderNumber not available
        await fetchOrderDetails(orderId);
      }
    };

    processOrderConfirmation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, clearCart]);

  const handleContinueShopping = () => {
    navigate('/menu');
  };

  const handleReturnHome = () => {
    navigate('/');
  };

  const renderContent = () => {
    switch (orderStatus) {
      case 'loading':
        return (
          <>
            <h1 className="confirmation-title">Processing Order...</h1>
            <p className="confirmation-message">
              Please wait while we process your order confirmation.
            </p>
          </>
        );

      case 'failed':
        return (
          <>
            <h1 className="confirmation-title">Order Failed</h1>
            <p className="confirmation-message">
              We're sorry, but there was an issue processing your order. Please try again or contact support for assistance.
            </p>
          </>
        );
      
      case 'pending':
        return (
          <>
            <h1 className="confirmation-title">Order Processing</h1>
            <p className="confirmation-message">
              Your order is being processed. Please wait while we confirm your payment and prepare your order.
            </p>
          </>
        );
      
      case 'success':
      default:
        return (
          <>
            <h1 className="confirmation-title">Order Submitted Successfully!</h1>
            
            {isLoadingOrder && (
              <div className="order-details-container">
                <div className="order-content-wrapper loading">
                  <OrderSkeleton />
                </div>
              </div>
            )}

            {orderError && !isLoadingOrder && (
              <div className="order-error">
                <p>We couldn't load your order details right now, but your order was placed successfully!</p>
                <p className="error-detail">{orderError}</p>
                <p>You will receive a confirmation email with complete order details.</p>
              </div>
            )}

            {orderDetails && !isLoadingOrder && (
              <div className="order-details-container">
                <div className="order-content-wrapper loaded">
                  <OrderDisplay order={orderDetails} retailerId={successfulRetailerId || undefined} />
                </div>
              </div>
            )}

            {!orderDetails && !isLoadingOrder && !orderError && (
              <div className="confirmation-message">
                <p>Thank you for your order! You will receive a confirmation email shortly with your order details and next steps.</p>
                <p>We are processing your order and you will receive an email confirmation shortly with complete details.</p>
              </div>
            )}

            {/* Show skeleton loader when we don't have order details but haven't finished loading/retrying */}
            {!orderDetails && !orderError && (searchParams.get('orderNumber') || searchParams.get('order_number') || searchParams.get('orderId') || searchParams.get('order_id')) && (
              <div className="order-details-container">
                <div className="order-content-wrapper loading">
                  <OrderSkeleton />
                </div>
              </div>
            )}
          </>
        );
    }
  };

  return (
    <div className="order-confirmation-container">
      <Helmet>
        <title>Order Confirmation - Private</title>
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta name="description" content="Private order confirmation page - not for public access" />
      </Helmet>
      <div className="order-confirmation-content">
        {renderContent()}
        
        <div className="confirmation-actions">
          <button 
            className="confirmation-btn primary" 
            onClick={handleContinueShopping}
          >
            Continue Shopping
          </button>
          <button 
            className="confirmation-btn secondary" 
            onClick={handleReturnHome}
          >
            Return Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;